/**
 * The transport half of the YouTube Data API client: credentials, the access
 * token, request building and paging.
 *
 * Split from the library so the read methods above it stay about YouTube's
 * data model rather than about HTTP. It does not cache: every call is made
 * from inside a query, and TanStack Query owns what is kept and for how long
 * — see `app/queries.ts`. Two caches would only disagree.
 */

import type { Credentials } from '../config/defaults.ts';
import { Emitter } from '../lib/emitter.ts';
import { type AccessToken, requestAccessToken, revokeToken } from './youtube-auth.ts';
import { describeApiError } from './youtube-errors.ts';

const API = 'https://www.googleapis.com/youtube/v3';
/** The API's own page size cap for every endpoint the app uses. */
export const PAGE = 50;

export interface Profile {
  name: string;
  avatar: string | null;
  channelId: string | null;
}

export type LibraryEvents = {
  signin: [profile: Profile];
  signout: [];
};

export type Params = Record<string, string | number | undefined>;

export interface Page<T> {
  items?: T[];
  nextPageToken?: string;
}

export abstract class YouTubeClient extends Emitter<LibraryEvents> {
  profile: Profile | null = null;

  #creds: Credentials;
  #auth: AccessToken | null = null;

  constructor(creds: Credentials) {
    super();
    this.#creds = creds;
  }

  get creds(): Credentials {
    return this.#creds;
  }

  get configured(): boolean {
    return !!this.#creds.GOOGLE_CLIENT_ID;
  }

  get signedIn(): boolean {
    return !!this.#auth && Date.now() < this.#auth.expiry;
  }

  setCreds(creds: Credentials): void {
    this.#creds = creds;
  }

  /** Opens Google's consent popup. Must be called from a user gesture. */
  async signIn(): Promise<Profile> {
    if (!this.configured) throw new Error('No OAuth Client ID configured.');
    this.#auth = await requestAccessToken(this.#creds.GOOGLE_CLIENT_ID);
    this.profile = await this.fetchProfile();
    this.emit('signin', this.profile);
    return this.profile;
  }

  signOut(): void {
    revokeToken(this.#auth?.token ?? null);
    this.#auth = null;
    this.profile = null;
    this.emit('signout');
  }

  async api<T>(path: string, params: Params = {}): Promise<T> {
    const url = new URL(`${API}/${path}`);
    for (const [k, v] of Object.entries(params)) {
      if (v != null && v !== '') url.searchParams.set(k, String(v));
    }

    const useAuth = this.signedIn;
    if (!useAuth) {
      if (!this.#creds.YOUTUBE_API_KEY) {
        throw new Error('Sign in, or add an API key, to reach YouTube.');
      }
      url.searchParams.set('key', this.#creds.YOUTUBE_API_KEY);
    }

    const res = await fetch(url, {
      headers: useAuth ? { Authorization: `Bearer ${this.#auth?.token ?? ''}` } : {},
    });
    if (!res.ok) {
      if (res.status === 401) {
        this.#auth = null;
        this.emit('signout');
      }
      throw new Error(describeApiError(res.status, await detailOf(res)));
    }

    return (await res.json()) as T;
  }

  /** Walk a paged endpoint until it runs out or `max` items have arrived. */
  protected async pages<T>(path: string, params: Params, max: number): Promise<T[]> {
    const out: T[] = [];
    let pageToken = '';
    do {
      const r = await this.api<Page<T>>(path, { ...params, maxResults: PAGE, pageToken });
      out.push(...(r.items ?? []));
      pageToken = r.nextPageToken ?? '';
    } while (pageToken && out.length < max);
    return out;
  }

  protected abstract fetchProfile(): Promise<Profile>;
}

async function detailOf(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { message?: string } };
    return body.error?.message ?? '';
  } catch {
    return ''; /* the body was not JSON; the status alone has to do */
  }
}
